// Librería de customElements
import {
  format as formatRut,
  validate as validateRut,
  clean as cleanRut,
} from './rut.js'

const style = document.createElement('style')
document.body.appendChild(style)
style.innerHTML = `
.pivot-field__error {
    display: none;
}
.pivot-field--is-invalid .pivot-field__error {
    display: block;
    font-size: .8em;
}
`
console.log(style)

// Fetching de datos
export const PivotFetchHandler = async (url, config = {}) => {
  if (!config.headers) config.headers = {}
  config.headers['content-type'] = 'application/json'
  if (config.body) config.body = JSON.stringify(config.body)
  const req = await fetch(url, config)
  const res = await req.json()
  if (!req.ok) throw new Error(res?.error?.message || res)
  return res
}

// Elemento base (con atributos reactivos)
// this.attribute: valor actual
// this._attribute: valor anterior
export class PivotElement extends HTMLElement {
  static EVENTS = {
    CHANGE_ATTRIBUTE: 'pivot-change-attribute',
  }
  #state = {}
  #customProperty = {}

  constructor() {
    super()
    // Crear atributos reactivos
    Object.entries(this.getAttributes()).forEach(([k, v]) => {
      this.#makeReactiveAttribute(k, v)
    })
  }

  static GetAttributes(element) {
    const attributes = element.attributes
    if (!attributes) return null
    return Object.assign(
      {},
      ...Array.from(element.attributes, ({ name, value }) => ({
        [name]:
          value.trim() === '' || value === 'true'
            ? true
            : value === 'false'
            ? false
            : value,
      }))
    )
  }

  showReactivity() {
    console.log(this.#state)
  }

  // Crear atributo reactivo arbitrariamente
  makeAttribute(name, value, subscriber) {
    this.#makeReactiveAttribute(name, value)
    if (subscriber) this.subscribe(name, subscriber)
  }

  // Setear reactividad customizada
  setCustomReactivity({ name, get, set }) {
    if (!this.#customProperty[name]) this.#customProperty[name] = {}
    if (get) this.#customProperty[name].get = get
    if (set) this.#customProperty[name].set = set
  }

  // Suscribirme al cambio de un atributo
  subscribe(attribute, subscriber, init = false) {
    if (this.#state[attribute] === undefined) {
      this.#makeReactiveAttribute(attribute, '')
    }
    this.addEventListener(
      PivotElement.EVENTS.CHANGE_ATTRIBUTE,
      ({ detail }) => {
        if (attribute === detail.attribute) subscriber(detail[attribute])
      }
    )
    if (init) subscriber(this[attribute])
  }

  // Obtener objeto de atributos
  getAttributes() {
    return PivotElement.GetAttributes(this)
  }

  // Registrar Getter y Setter
  #makeReactiveAttribute(name, defaultValue) {
    if (this.#state[name] !== undefined) return
    if (name === 'class') return

    this.#state[name] = defaultValue
    Object.defineProperty(this, name, {
      configurable: true,
      set(val) {
        this[`_${name}`] = this.#state[name]
        this.#state[name] = val
        if (val === true) this.setAttribute(name, true)
        if (val === false) this.removeAttribute(name)

        if (this.#customProperty[name]?.set) this.#customProperty[name].set(val)
        this.dispatchEvent(
          new CustomEvent(PivotElement.EVENTS.CHANGE_ATTRIBUTE, {
            bubbles: true,
            detail: { [name]: val, attribute: name },
          })
        )
      },
      get() {
        if (this.#customProperty[name]?.get) {
          return this.#customProperty[name].get()
        }
        return this.#state[name]
      },
    })
  }
}

// Pivot Field
export class PivotField extends PivotElement {
  static EVENTS = {
    INVALID: 'invalid',
    VALIDATED: 'validated',
    LOADING: 'loading',
    LOADED: 'loaded',
  }
  static InvalidClass = 'pivot-field--is-invalid'
  #previousMarkup = this.innerHTML

  constructor() {
    super()
    const { label, name, value } = this.getAttributes()

    // Crear Atributo Label
    if (!label) this.makeAttribute('label', this.textContent || name || '')

    // Crear atributo Value
    if (!this.value) this.makeAttribute('value', '')
  }

  // Método para validar el field
  get validate() {
    const regexp = this.pattern ? new RegExp(this.pattern) : null
    const minLength = Number(this.minlength || 0)
    return () => {
      const validLength = this.value.length >= minLength
      const invalid = this.$input.matches(':invalid')
      const tested = regexp ? regexp?.test(this.value) : true
      const customValidation = this.customValidation(this.value)
      const invalidStatus =
        validLength && (!tested || invalid || !customValidation)
      this.invalid = invalidStatus
    }
  }

  set validate(val) {
    console.error('El método validate es de solo lectura', val)
  }

  connectedCallback() {
    if (!this.isConnected) return
    const {
      'omit-autorender': autoRender,
      pattern,
      value,
      ...attrs
    } = this.getAttributes()
    // AutoRenderizado
    if (!this['omit-autorender']) this.#render(attrs)
    this.$input = this.querySelector(
      '.field__input, .select__select, .field__radio'
    )
    this.$spinner = this.querySelector('.pivot-loading')

    // Suscripciones
    this.subscribe('loading', this.#onLoading)
    this.subscribe(
      'required',
      isRequired => (this.$input.required = isRequired)
    )
    this.subscribe('disabled', this.#onDisabled, true)
    this.subscribe('invalid', this.#onInvalid, true)

    // Validacion
    this.#initValidation()

    // Propagar eventos
    this.$input.addEventListener('focus', () => {
      this.dispatchEvent(
        new CustomEvent('focus', {
          bubbles: true,
        })
      )
    })

    this.setCustomReactivity({
      name: 'value',
      get: () => this.$input?.value,
      set: value => {
        this.$input ? (this.$input.value = value) : false
      },
    })
  }

  focus() {
    this.$input.focus()
  }

  #initValidation() {
    this.addEventListener('input', this.validate)
  }

  customValidation() {
    return true
  }

  #render(attributes) {
    this.classList.add('pivot-field')
    this.innerHTML = `
      <div class="${this.type === 'select' ? 'select field' : 'field'}">
        ${this.#fieldMarkup(attributes)}
        ${this.#labelMarkup()}
      </div>
      <span class="pivot-field__error">${this['error-message'] || ''}</span>`
  }

  #fieldMarkup(attributes) {
    const renderAttributes = Object.entries(attributes)
      .map(([k, v]) => `${k}="${v}"`)
      .join(' ')

    if (this.type === 'textarea') {
      return `<textarea
        type="text"
        class="texarea field__input"
        id="${this.id}-field"
        name="${this.name}"
        placeholder="${this.label || ''}"
        aria-required="true"
        autocapitalize="off"
        placeholder="${this.label || ''}"
        ${this.required ? 'required' : ''}
        ${this.disabled ? 'disabled' : ''}
        control-id="ControlID-2"
        ${renderAttributes}
        style="resize: none; min-height: 100px;"
      >${this.value === true ? '' : this.value || ''}</textarea>`
    }
    if (this.type === 'select') {
      return `
        <select
          id="${this.id}-select"
          class="select__select field__input"
          name="${this.name}"
          value="${this.value === true ? '' : this.value || ''}"
          ${this.required ? 'required' : ''}
          ${this.disabled ? 'disabled' : ''}
          control-id="ControlID-2"
          ${renderAttributes}
        >
          ${this.#previousMarkup}
        </select>
        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"> </path>
        </svg>`
    }
    return `<input
      type="${this.type || 'text'}"
      id="${this.id}-field"
      name="${this.name}"
      class="field__input"
      aria-required="true"
      autocorrect="off"
      autocapitalize="off"
      placeholder="${this.label || ''}"
      value="${this.value === true ? '' : this.value || ''}"
      ${this.required ? 'required' : ''}
      ${this.disabled ? 'disabled' : ''}
      control-id="ControlID-2"
      ${renderAttributes}
    >`
  }

  #labelMarkup() {
    if (!this.label) return ''
    return `
    <label for="${this.id}-field" class="field__label">
      ${this.label || ''}
    </label>
    <div class="pivot-loading hidden" id="${this.id}--loading">
      <div class="loading-overlay__spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    </div>`
  }

  #onDisabled = isDisabled => {
    this.classList.toggle('field--disabled', isDisabled)
    this.$input.disabled = isDisabled
  }
  #onLoading = isLoading => {
    if (this.$spinner) {
      this.$spinner.classList.toggle('hidden', !isLoading)
    }
    this.disabled = isLoading
    this.dispatchEvent(
      new CustomEvent(
        isLoading ? PivotField.EVENTS.LOADING : PivotField.EVENTS.LOADED,
        { bubbles: true }
      )
    )
  }
  #onInvalid = isInvalid => {
    const invalidCss = this.$input.matches(':invalid')
    this.classList.toggle(PivotField.InvalidClass, isInvalid)

    // Notificar que es inválido (solo cuando se escribió suficiente)
    if (isInvalid) {
      this.dispatchEvent(
        new CustomEvent(PivotField.EVENTS.INVALID, { bubbles: true })
      )
      return
    }
    // Notificar que es válido (en todos los sentidos)
    if (!invalidCss) {
      this.dispatchEvent(
        new CustomEvent(PivotField.EVENTS.VALIDATED, { bubbles: true })
      )
    }
  }
}

if (!customElements.get('pivot-field')) {
  customElements.define('pivot-field', PivotField)
}

// Input del rut
export class PivotRutField extends PivotField {
  static EVENTS = {
    ...PivotField.EVENTS,
    VALID_RUT: 'valid-rut',
  }
  'error-message' = 'Indique un rut válido'
  minlength = 9

  connectedCallback() {
    super.connectedCallback()
    // Soportar solo caracteres válidos
    this.addEventListener('keydown', e => {
      const isCopy = e.key === 'c' && e.ctrlKey
      const isPaste = e.key === 'v' && e.ctrlKey
      if (isPaste || isCopy) return
      if (e.key !== 'Tab' && !isCopy && !isPaste && !/[0-9kKoO]/.test(e.key)) {
        e.preventDefault()
      }
    })
  }

  customValidation(value) {
    const rut = formatRut(value)
    this.value = rut === '-' ? '' : rut

    const minLength = 9
    if (rut.length < minLength || !validateRut(rut)) return false

    this.dispatchEvent(
      new CustomEvent(PivotRutField.EVENTS.VALID_RUT, {
        detail: { rut: rut, cleanRut: cleanRut(rut) },
      })
    )
    return true
  }

  get isValid() {
    return validateRut(this.value)
  }
  get cleanValue() {
    return cleanRut(this.value)
  }
  get formattedValue() {
    return formatRut(this.value)
  }
}

if (!customElements.get('pivot-rut-field')) {
  customElements.define('pivot-rut-field', PivotRutField)
}

// Formulario (wrapper)
export class PivotForm extends HTMLFormElement {
  static EVENTS = {
    SENT: 'sent',
    FINALLY: 'finally',
    ERROR: 'error',
  }
  validationMode = 'on-input'
  fetchHandler = PivotFetchHandler
  action = undefined

  constructor() {
    super()
    this.$fields = this.querySelectorAll('pivot-field, .pivot-field')
    this.$button =
      this.querySelector('[pivot-action]') || document.createElement('button')
    this.$buttonLoader = this.$button.querySelector('.loading-overlay__spinner')
    const validator = () => {
      const invalid = this.querySelector(
        `:invalid, .${PivotField.InvalidClass}`
      )
      const enabledField = this.querySelector(
        ':is(input,select,textarea):not(:disabled)'
      )
      if (this.disabled || invalid || !enabledField) {
        this.$button.disabled = true
        return
      }
      this.$button.disabled = false
    }
    if (this.validationMode === 'on-input') {
      this.addEventListener('input', validator)
      this.addEventListener('change', validator)
    }
    this.addEventListener('submit', e => {
      e.preventDefault()
      const invalid = this.querySelector(
        `:invalid, .${PivotField.InvalidClass}`
      )
      if (invalid) {
        invalid.focus()
        return
      }

      if (this.action && this.fetchHandler) {
        this.#setLoading()
        this.fetchHandler(this.action, {
          method: this.method || 'get',
          headers: this.headers || {},
          body: this.value,
        })
          .then(res => {
            this.dispatchEvent(
              new CustomEvent(PivotForm.EVENTS.SENT, {
                detail: res,
                bubbles: true,
              })
            )
          })
          .catch(err => {
            console.error(err)
            this.dispatchEvent(
              new CustomEvent(PivotForm.EVENTS.ERROR, {
                detail: err,
                bubbles: true,
              })
            )
          })
          .finally(() => {
            this.#unsetLoading()
            this.dispatchEvent(
              new CustomEvent(PivotForm.EVENTS.FINALLY, { bubbles: true })
            )
          })
      } else {
        this.dispatchEvent(
          new CustomEvent(PivotForm.EVENTS.SENT, {
            detail: this.value,
            bubbles: true,
          })
        )
      }
    })
  }

  get value() {
    return Object.fromEntries(new FormData(this))
  }

  enable(focus) {
    this.$fields.forEach(field => (field.disabled = false))
    this.disabled = false
    this.querySelector(`[name="${focus}"]`)?.focus()
  }

  disable() {
    this.$fields.forEach(field => (field.disabled = true))
  }

  #setLoading() {
    this.$button.disabled = true
    if (this.$buttonLoader) this.$buttonLoader.classList.remove('hidden')
  }

  #unsetLoading() {
    this.$button.disabled = false
    if (this.$buttonLoader) this.$buttonLoader.classList.add('hidden')
  }
}

if (!customElements.get('pivot-form')) {
  customElements.define('pivot-form', PivotForm, { extends: 'form' })
}

// Stepper (Manejador de Multiples pasos)
class Stepper extends PivotElement {
  #activeClass = 'pivot-stepper-active'
  #itemSelector = 'pivot-stepper-tabs-item'
  #panelSelector = 'pivot-stepper-panel'
  #EVENTS = {
    CHANGE: 'change',
    SELECTED: 'selected',
    UNSELECT: 'unselect',
  }

  constructor() {
    super()
    this.tabs = [...this.querySelectorAll(this.#itemSelector)]
    this.panels = [...this.querySelectorAll(this.#panelSelector)]

    // Registrar un atributo
    this.makeAttribute('activeStep', 0, this.#onChange)

    // DevMode
    if (this['dev-mode'] || Shopify.designMode) {
      console.warn(
        'PIVOT STEPPER: Está activado el DEV_MODE, esto no es para producción, asegurese de desactivarlo'
      )
      this.tabs.forEach((tb, idx) => {
        tb.addEventListener('click', () => (this.activeStep = idx))
      })
    }
  }

  // Api
  #onChange = value => {
    const newSelection = this.tabs[value]
    if (!newSelection) return

    const oldSelection = this.tabs[this._activeStep]
    if (oldSelection) {
      oldSelection.classList.remove(this.#activeClass)
      const oldPanel = this.panels.find(
        p => p.id === oldSelection.getAttribute('panel')
      )
      if (oldPanel) {
        oldPanel.classList.remove(this.#activeClass)
        oldPanel.dispatchEvent(
          new CustomEvent(this.#EVENTS.UNSELECT, { bubbles: true })
        )
      }
    }

    newSelection.classList.add(this.#activeClass)
    const panel = this.panels.find(
      p => p.id === newSelection.getAttribute('panel')
    )
    if (panel) {
      panel.classList.add(this.#activeClass)
      panel.dispatchEvent(
        new CustomEvent(this.#EVENTS.SELECTED, { bubbles: true })
      )
    }
    this.dispatchEvent(
      new CustomEvent(this.#EVENTS.CHANGE, {
        bubbles: true,
        detail: { activeStep: this.activeStep },
      })
    )
  }

  canNextStep() {
    const newIndex = this.activeStep + 1
    return !!this.tabs[newIndex]
  }

  canPrevStep() {
    return this.activeStep > 0
  }

  nextStep() {
    if (this.canNextStep()) this.activeStep = this.activeStep + 1
  }

  prevStep() {
    if (this.canPrevStep()) this.activeStep = this.activeStep - 1
  }
}

if (!customElements.get('pivot-stepper')) {
  customElements.define('pivot-stepper', Stepper)
}

// Modal
class PivotModal extends PivotElement {
  constructor() {
    super()
    this.classList.add('pivot-modal')
    this.innerHTML = `
    <article class="pivot-modal__container">
      <header class="pivot-modal__header">
        <h2 class="pivot-modal__title">${this.title}</h2>
        <button class="pivot-modal__close-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
            class="icon icon-close"
            fill="none"
            viewBox="0 0 18 17"
          >
            <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
          </svg>
        </button>
      </header>
      <div class="pivot-modal__content"><p>${this.content}</p></div>
      <button class="button button--primary" id="ok">Entendido</button>
    </article>
    `
    this.$title = this.querySelector('.pivot-modal__title')
    this.$content = this.querySelector('.pivot-modal__content')
    this.subscribe('title', title => (this.$title.textContent = title))
    this.subscribe('content', content => (this.$content.innerHTML = content))
    this.subscribe('open', isOpen => {
      this.classList.toggle('pivot-modal--visible', Boolean(isOpen))
    })

    this.addEventListener('click', e => {
      if (
        e.target === this ||
        e.target.id === 'ok' ||
        e.target.classList.contains('pivot-modal__close-button')
      ) {
        this.closeModal()
      }
    })
  }

  show({ title, content }) {
    if (title) this.title = title
    if (content) this.content = content
    this.openModal()
  }

  openModal() {
    this.open = true
  }

  closeModal() {
    this.open = false
  }
}

if (!customElements.get('pivot-modal')) {
  customElements.define('pivot-modal', PivotModal)
}

